import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawRopeSpan, drawTimberBoards } from './propDrawingPrimitives';
import { drawFishingNet, drawRopeCoil } from './maritimeDrawingPrimitives';

export type HarborFishingTemplate = 'crab_pot_stack' | 'fish_market_counter' | 'life_ring_post' | 'coiled_rope_basket';

function crabPot(x: number, y: number, height: number, p: TownPropPalette): string {
  return `<g data-part="crab-pot" transform="translate(${x} ${y})">
    ${drawFishingNet({ x: 6, y: 12, width: 35, height: height - 18 }, p, { columns: 5, rows: 3 })}
    <path d="M3 13C3 5 13 2 23.5 2C34 2 44 5 44 13V${height - 7}Q23.5 ${height + 1} 3 ${height - 7}Z"
      fill="none" stroke="${hex(p.iron)}" stroke-width="3" />
    <path d="M3 13C3 5 13 2 23.5 2C34 2 44 5 44 13V${height - 7}Q23.5 ${height + 1} 3 ${height - 7}Z
      M3 13Q23.5 21 44 13 M8 16V${height - 5} M23.5 19V${height - 3} M39 16V${height - 5}"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
    <ellipse data-part="pot-entry" cx="23.5" cy="10" rx="8" ry="4.5" fill="none" stroke="${hex(p.wood)}" stroke-width="2.3" />
    <path d="M16 10Q23.5 5 31 10" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.1" />
    <path d="M6 ${height - 3}H41V${height}H6Z" fill="${hex(p.wood)}" />
    <path d="M8 ${height - 2}H38" stroke="${hex(p.woodEdge)}" stroke-width="0.8" />
    <path d="M33 18L38 18L40 28L35 30Z" fill="${hex(p.primary)}" />
    <circle cx="36" cy="21" r="1.1" fill="${hex(p.iron)}" />
  </g>`;
}

/** 100×84. Open meshes and entry funnels distinguish three stacked traps. */
function crabPots(p: TownPropPalette): string {
  return `${crabPot(26, 10, 39, p)}${crabPot(3, 44, 40, p)}${crabPot(50, 46, 38, p)}
    <g data-part="pot-stack-ties">
      ${drawRopeSpan({ x: 29, y: 42 }, { x: 52, y: 49 }, p, { sag: 3, thickness: 1.8 })}
      ${drawRopeSpan({ x: 70, y: 42 }, { x: 67, y: 49 }, p, { sag: 2, thickness: 1.6 })}
    </g>`;
}

function fish(x: number, y: number, rotation: number, p: TownPropPalette): string {
  return `<g data-part="market-fish" transform="translate(${x} ${y}) rotate(${rotation})">
    <path d="M-12 0L-22 -6L-20 0L-22 6Z" fill="${hex(p.primary)}" stroke="${hex(p.iron)}" stroke-width="0.7" />
    <path d="M-14 0Q-4 -9 8 -5L16 0L8 5Q-4 9 -14 0Z" fill="${hex(p.stone)}" stroke="${hex(p.iron)}" stroke-width="0.8" />
    <path d="M-10 1Q0 7 12 1Q1 3 -10 1Z" fill="${hex(p.glassCore)}" />
    <path d="M-4 -5L1 -10L5 -5 M-1 5L4 9L7 5" fill="${hex(p.primary)}" />
    <path d="M8 -4Q5 0 8 4 M-8 -1L-2 -3 M-3 1L2 -1" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="0.8" />
    <circle cx="11" cy="-1.5" r="1.3" fill="${hex(p.iron)}" />
  </g>`;
}

/** 144×96. A fish tray, wordless priceboard and two separate planted legs. */
function fishCounter(p: TownPropPalette): string {
  const wood = { ...p, primary: p.wood };
  return `<g data-part="fish-counter-frame">
    ${drawTimberBoards({ x: 14, y: 46, width: 12, height: 50 }, wood, { axis: 'vertical', boards: 1 })}
    ${drawTimberBoards({ x: 118, y: 46, width: 12, height: 50 }, wood, { axis: 'vertical', boards: 1 })}
    <path d="M22 75L44 53 M99 53L122 75" fill="none" stroke="${hex(p.wood)}" stroke-width="5" />
    <path d="M14 91H26V96H14Z M118 91H130V96H118Z" fill="${hex(p.iron)}" />
    <path d="M10 32H132L139 42H5Z" fill="${hex(p.woodEdge)}" />
    ${drawTimberBoards({ x: 7, y: 42, width: 130, height: 15 }, p, { boards: 2 })}
    <path d="M9 35H132 M29 33L26 41 M56 33L54 41 M84 33L84 41 M110 33L112 41"
      stroke="${hex(p.wood)}" stroke-width="1.1" />
    <path d="M5 41H139V45H5Z" fill="${hex(p.wood)}" />
    <path d="M9 43H135" stroke="${hex(p.woodEdge)}" stroke-width="1" />
    <circle cx="20" cy="49" r="1.4" fill="${hex(p.iron)}" /><circle cx="124" cy="49" r="1.4" fill="${hex(p.iron)}" />
  </g>
  <g data-part="fish-ice-tray">
    <path d="M13 31L99 28L111 39L13 41Z" fill="${hex(p.iron)}" />
    <path d="M17 32L24 25L36 30L46 24L58 31L66 26L79 30L88 25L98 31L107 37L17 39Z" fill="${hex(p.stone)}" />
    <path d="M18 33L24 28L30 31L25 35Z M44 30L47 27L53 30L49 34Z M75 33L80 30L85 34L80 38Z M94 33L98 31L102 36L97 38Z"
      fill="${hex(p.glassCore)}" opacity="0.65" />
    ${fish(35, 31, -12, p)}${fish(72, 30, 8, p)}${fish(93, 37, -8, p)}
    <path d="M13 39H110V43H13Z" fill="${hex(p.woodEdge)}" />
  </g>
  <g data-part="fish-priceboard">
    <path d="M120 29H125V41H120Z" fill="${hex(p.wood)}" />
    <path d="M109 6L136 8L134 34L107 32Z" fill="${hex(p.woodEdge)}" />
    <path d="M112 10L132 11L131 30L111 29Z" fill="${hex(p.iron)}" />
    <path d="M116 17L113 14V20Z M116 17Q122 11 128 18Q121 22 116 17Z" fill="${hex(p.stone)}" />
    <circle cx="118" cy="25" r="1.6" fill="${hex(p.glassCore)}" /><circle cx="125" cy="25" r="1.6" fill="${hex(p.glassCore)}" />
  </g>`;
}

/** 52×112. The post remains visible through the ring's genuinely open center. */
function lifeRing(p: TownPropPalette): string {
  const wood = { ...p, primary: p.wood };
  return `<g data-part="safety-post">
    ${drawTimberBoards({ x: 22, y: 8, width: 10, height: 104 }, wood, { axis: 'vertical', boards: 1 })}
    <path d="M19 9L27 3L35 9V13H19Z" fill="${hex(p.primary)}" />
    <path d="M19 104H35L39 108V112H15V108Z" fill="${hex(p.stone)}" />
    <path d="M15 109H39V112H15Z" fill="${hex(p.iron)}" />
    <path d="M27 17H35Q41 17 39 24L34 30" fill="none" stroke="${hex(p.iron)}" stroke-width="3.4" stroke-linecap="round" />
    <path d="M22 18H32V22H22Z" fill="${hex(p.iron)}" />
  </g>
  <g data-part="life-ring" fill="none">
    <circle cx="26" cy="47" r="24" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
    <circle cx="26" cy="47" r="18" stroke="${hex(p.glassCore)}" stroke-width="8" />
    <path data-part="ring-segments" d="M19.84 30.09A18 18 0 0 1 32.16 30.09 M42.91 40.84A18 18 0 0 1 42.91 53.16
      M32.16 63.91A18 18 0 0 1 19.84 63.91 M9.09 53.16A18 18 0 0 1 9.09 40.84"
      stroke="${hex(p.primary)}" stroke-width="8" />
    <circle cx="26" cy="47" r="22" stroke="${hex(p.iron)}" stroke-width="0.9" />
    <circle cx="26" cy="47" r="14" stroke="${hex(p.iron)}" stroke-width="0.8" />
    <path d="M26 23V29 M44 47H50 M26 65V71 M2 47H8" stroke="${hex(p.woodEdge)}" stroke-width="1.6" />
  </g>
  <g data-part="safety-rope">
    ${drawRopeSpan({ x: 12, y: 66 }, { x: 34, y: 94 }, p, { sag: 7, thickness: 2 })}
    <path d="M32 92L36 95L34 98" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.6" />
  </g>`;
}

/** 84×64. Coils crest the basket rim; woven sides and flat runners support it. */
function ropeBasket(p: TownPropPalette): string {
  return `<g data-part="rope-basket">
    <path d="M12 34Q0 29 5 18Q8 12 16 20 M72 34Q84 29 79 18Q76 12 68 20"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2.6" />
    <path d="M7 29H77L70 58Q42 70 14 58Z" fill="${hex(p.wood)}" />
    ${drawFishingNet({ x: 16, y: 35, width: 52, height: 23 }, p, { columns: 8, rows: 3 })}
    <path d="M11 38Q42 46 73 38 M13 47Q42 55 71 47 M17 55Q42 63 67 55"
      fill="none" stroke="${hex(p.primary)}" stroke-width="2" />
    <ellipse cx="42" cy="29" rx="35" ry="10" fill="${hex(p.iron)}" stroke="${hex(p.woodEdge)}" stroke-width="2" />
    <path d="M23 61H61V64H23Z" fill="${hex(p.wood)}" />
  </g>
  <g data-part="basket-coils">
    ${drawRopeCoil({ x: 12, y: 5, width: 60, height: 30 }, p, { turns: 5 })}
    ${drawRopeCoil({ x: 23, y: 20, width: 36, height: 18 }, p, { turns: 3 })}
    ${drawRopeSpan({ x: 66, y: 25 }, { x: 72, y: 42 }, p, { sag: 8, thickness: 2.6 })}
  </g>
  <path d="M7 29Q42 49 77 29" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="3.5" />
  <path d="M12 33L17 37 M26 38L31 40 M42 40L47 39 M58 37L63 35" stroke="${hex(p.wood)}" stroke-width="1.2" />`;
}

/** Static bottom-anchored scenery; buying, fishing and safety interactions are not supplied by art. */
export function buildHarborFishingBody(template: HarborFishingTemplate, p: TownPropPalette): string {
  switch (template) {
  case 'crab_pot_stack': return crabPots(p);
  case 'fish_market_counter': return fishCounter(p);
  case 'life_ring_post': return lifeRing(p);
  case 'coiled_rope_basket': return ropeBasket(p);
  }
}
